import React from 'react';

/**
 * SvgBoxWithStrips - Striped Rectangle Pattern Component
 * 
 * Renders an SVG rectangle filled with diagonal stripe pattern.
 * Used to represent walls, insulation, materials, or textured surfaces in educational diagrams.
 * 
 * The component creates a unique SVG pattern definition and applies it to a rectangle.
 * The pattern consists of evenly-spaced parallel lines at a configurable angle.
 * 
 * Pattern characteristics:
 * - 10px x 10px repeating pattern
 * - Vertical lines rotated to desired angle
 * - Configurable color and transparency
 * - Unique pattern ID to avoid conflicts in complex SVGs
 * 
 * @component
 * @param {Object} props - Component properties
 * @param {number} props.W - Width of the rectangle (excluding padding)
 * @param {number} props.H - Height of the rectangle (excluding padding)
 * @param {number} [props.X=0] - X coordinate position in parent SVG
 * @param {number} [props.Y=0] - Y coordinate position in parent SVG
 * @param {number} [props.angle=45] - Rotation angle of stripe pattern in degrees (0-360)
 * @param {string} [props.color='black'] - Color of stripes (CSS color value)
 * @param {string} [props.patternId] - Optional custom ID for the pattern definition
 *                                      If not provided, auto-generated from position and size
 * 
 * @returns {JSX.Element} SVG element with striped rectangle pattern
 * 
 * @example
 * // Wall with 45-degree diagonal stripes
 * <SvgBoxWithStrips 
 *   W={20} 
 *   H={250} 
 *   X={30} 
 *   Y={20} 
 *   angle={45} 
 *   color="#0B75BD" 
 *   patternId="left-wall" 
 * />
 * 
 * @example
 * // Horizontal stripes (0-degree angle)
 * <SvgBoxWithStrips W={100} H={50} X={0} Y={0} angle={0} color="red" />
 */
export function SvgBoxWithStrips({ W, H, X = 0, Y = 0, angle = 45, color = 'black', patternId }) {
  // Generate unique pattern ID if not provided (prevents pattern conflicts in complex SVGs)
  const uniquePatternId = patternId || `stripes-${X}-${Y}-${W}-${H}`;

  return (
    // Container SVG with 10px padding on all sides
    <svg 
      width={W + 20} 
      height={H + 20} 
      x={X} 
      y={Y} 
      viewBox={`0 0 ${W + 20} ${H + 20}`} 
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Pattern definition */}
      <defs>
        {/* 
          Create repeating stripe pattern:
          - 10x10 unit size
          - Rotated by specified angle around center point (10, 10)
          - Vertical lines that appear diagonal after rotation
        */}
        <pattern 
          id={uniquePatternId} 
          patternUnits="userSpaceOnUse" 
          width="10" 
          height="10" 
          patternTransform={`rotate(${angle} 10 10)`}
        >
          {/* Single vertical line (0,0 to 0,10) creates stripe when repeated */}
          <line x1="0" y1="0" x2="0" y2="10" stroke={color} strokeWidth="1" />
        </pattern>
      </defs>

      {/* Rectangle outline (border) */}
      <rect x="10" y="10" width={W} height={H} stroke={color} fill="none" />
      
      {/* Rectangle filled with stripe pattern (semi-transparent) */}
      <rect 
        x="10" 
        y="10" 
        width={W} 
        height={H} 
        fill={`url(#${uniquePatternId})`} 
        opacity="0.8" 
      />
    </svg>
  );
}

export default SvgBoxWithStrips;
